/**
 * target_type mapping seam (FIR-6995).
 *
 * The unified opportunities endpoint keys every vertical on its
 * `unified_target_type`. The app refers to verticals by two other vocabularies
 * that do NOT always match the backend value:
 *
 *  - the page "vertical" name (e.g. `convenience`, `nursinghome`)
 *  - the saved-view sub-category slug (e.g. `convenience`, `healthcare-nursinghomes`)
 *
 * Three names genuinely differ (per `UNIFIED_DATA_TYPES_MANUAL.md` §1, the #1
 * source of NULL-bucketed rows): `convenience → cstore`, k12 variants → `k12`,
 * nursing-home variants → `nursing_home`. The rest map to themselves.
 *
 * Every unified API call MUST route its `target_type` through {@link getTargetType}
 * so no `target_type` literal ever appears at a call site.
 */

/** The canonical backend `unified_target_type` values. */
export type UnifiedTargetType =
  | 'restaurant'
  | 'lodging'
  | 'cstore'
  | 'university'
  | 'k12'
  | 'hospital'
  | 'nursing_home'

/**
 * Map of every known app vertical name / sub-category slug → backend
 * `unified_target_type`. Identity entries are included explicitly so a lookup
 * miss is unambiguous (returns `undefined`, never a silently-wrong value).
 */
export const VERTICAL_TO_TARGET_TYPE: Readonly<
  Record<string, UnifiedTargetType>
> = {
  // restaurant
  restaurant: 'restaurant',
  'restaurants-bars': 'restaurant',
  // lodging
  lodging: 'lodging',
  'lodging-hospitality': 'lodging',
  // convenience → cstore (naming mismatch)
  convenience: 'cstore',
  cstore: 'cstore',
  // university
  university: 'university',
  'education-cu': 'university',
  // k12 (naming mismatch)
  k12: 'k12',
  k12district: 'k12',
  'education-k-12': 'k12',
  // hospital
  hospital: 'hospital',
  'healthcare-hospitals': 'hospital',
  // nursing_home (naming mismatch)
  nursing_home: 'nursing_home',
  nursinghome: 'nursing_home',
  'healthcare-nursinghomes': 'nursing_home',
}

/**
 * Resolve an app vertical name or sub-category slug to its backend
 * `unified_target_type`. Throws on an unknown key so a misconfigured call site
 * fails loudly rather than querying the wrong vertical.
 */
export function getTargetType(vertical: string): UnifiedTargetType {
  const targetType = VERTICAL_TO_TARGET_TYPE[vertical]
  if (targetType === undefined) {
    throw new Error(`Unknown opportunities vertical: "${vertical}"`)
  }
  return targetType
}
